import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import { withStyles } from '@material-ui/core/styles'
import {
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Input,
  IconButton,
} from '@material-ui/core'
import { mdiContentSave, mdiPencil } from '@mdi/js'

import Link from '../controls/Link'
import { timeseriesConfig } from '../../utils/TimeseriesConfig'
import TimeseriesTypesSelectContainer from '../controls/TimeseriesTypesSelectContainer'
import DeviceSelectContainer from '../controls/DeviceSelectContainer'
import SensorSelectContainer from '../controls/SensorSelectContainer'

class TimeseriesListView extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    timeseries: PropTypes.object,
    onSave: PropTypes.func,
  }

  static defaultProps = {
    timeseries: {},
    onSave: null,
  }

  static styles = {
    icon: {
      textAlign: 'center',
    },
  }

  state = {
    isEditing: {},
    timeseries: this.props.timeseries,
    order: 'asc',
    orderBy: 'id',
  }

  componentWillReceiveProps(nextProps) {
    this.setState({ timeseries: nextProps.timeseries })
  }

  onChanges(id, changes) {
    this.setState(
      (prevState) => ({
        timeseries: {
          ...prevState.timeseries,
          [id]: {
            ...prevState.timeseries[id],
            ...changes,
          },
        },
      }),
    )
  }

  onChange(id, field, value) {
    this.onChanges(id, { [field]: value })
  }

  onCheckboxChange(id, e) {
    const { name, checked } = e.currentTarget
    this.onChange(id, name, checked)
  }

  onInputChange(id, e) {
    const { value, name } = e.currentTarget
    this.onChange(id, name, value)
  }

  onDeviceChange(id, value) {
    this.onChanges(id, { device_id: value, sensor_id: '' })
  }

  onSensorChange(id, value) {
    this.onChange(id, 'sensor_id', value)
  }

  onEditClick(id) {
    this.setState(
      (prevState) => ({
        isEditing: { ...prevState.isEditing, [id]: true },
      }),
    )
  }

  onSaveClick(id) {
    const { timeseries } = this.state

    this.setState(
      (prevState) => ({
        isEditing: { ...prevState.isEditing, [id]: false },
      }),
    )

    this.props.onSave(timeseries[id])
  }

  handleSort(orderBy) {
    let order = 'desc'

    if (this.state.orderBy === orderBy && this.state.order === 'desc') {
      order = 'asc'
    }
    this.setState({ order, orderBy })
  }

  sortedTimeseries() {
    const { orderBy, order } = this.state
    const timeseries = Object.values(this.state.timeseries)
    const sorted = order === 'desc'
      ? timeseries.sort((a, b) => (b[orderBy] < a[orderBy] ? -1 : 1))
      : timeseries.sort((a, b) => (a[orderBy] < b[orderBy] ? -1 : 1))

    return sorted
  }

  actionButton(id) {
    const { isEditing } = this.state
    let actionButtonHtml
    if (isEditing[id]) {
      actionButtonHtml = (
        <IconButton aria-label="Save" onClick={() => this.onSaveClick(id)}>
          <SvgIcon>
            <path d={mdiContentSave} />
          </SvgIcon>
        </IconButton>
      )
    } else {
      actionButtonHtml = (
        <IconButton aria-label="Edit" onClick={() => this.onEditClick(id)}>
          <SvgIcon>
            <path d={mdiPencil} />
          </SvgIcon>
        </IconButton>
      )
    }
    return actionButtonHtml
  }

  tableSortLabelHtml(property, label) {
    const { orderBy, order } = this.state

    return (
      <TableSortLabel
        active={orderBy === property}
        direction={order}
        onClick={() => this.handleSort(property)}
      >
        {label}
      </TableSortLabel>
    )
  }

  renderTimeseries = (ts) => {
    const { classes } = this.props
    const isEditing = this.state.isEditing[ts.id] || false
    const tsConfig = timeseriesConfig(ts.name)
    const lastUpdated = moment(ts.last_updated)

    return (
      <TableRow key={`${ts.id}`}>
        <TableCell padding="none" className={classes.icon}>
          <SvgIcon>
            <path d={tsConfig.icon} />
          </SvgIcon>
        </TableCell>
        <TableCell size="small">
          <Link to={`/units/${ts.unit_id}/timeseries/${ts.id}`}>
            {ts.id}
          </Link>
        </TableCell>
        <TableCell size="small">
          <TimeseriesTypesSelectContainer
            autoWidth
            disabled={!isEditing}
            value={ts.name}
            sensorType={ts.type}
            onChange={(val) => this.onChange(ts.id, 'name', val)}
          />
        </TableCell>
        <TableCell size="small">
          <DeviceSelectContainer
            autoWidth
            disabled={!isEditing}
            unitId={ts.unit_id}
            value={ts.device_id}
            onChange={(val) => this.onDeviceChange(ts.id, val)}
          />
        </TableCell>
        <TableCell size="small">
          <SensorSelectContainer
            autoWidth
            disabled={!isEditing}
            deviceId={ts.device_id}
            value={ts.sensor_id}
            onChange={(val) => this.onSensorChange(ts.id, val)}
          />
        </TableCell>
        <TableCell padding="checkbox">
          <Checkbox
            disabled={!isEditing}
            name="internal"
            checked={ts.internal}
            onChange={(e) => this.onCheckboxChange(ts.id, e)}
          />
        </TableCell>
        <TableCell align="right" size="small">
          {lastUpdated.unix() > 0 ? tsConfig.format(ts.current_value.value) : '-'}
        </TableCell>
        <TableCell size="small">{lastUpdated.unix() > 0 ? lastUpdated.calendar() : '-'}</TableCell>
        <TableCell size="small">
          <Input
            multiline
            disabled={!isEditing}
            name="description"
            value={ts.description}
            onChange={(e) => this.onInputChange(ts.id, e)}
          />
        </TableCell>
        <TableCell padding="checkbox">
          <Checkbox
            disabled={!isEditing}
            name="archived"
            checked={ts.archived}
            onChange={(e) => this.onCheckboxChange(ts.id, e)}
          />
        </TableCell>
        <TableCell padding="none">
          {this.actionButton(ts.id)}
        </TableCell>
      </TableRow>
    )
  }

  render() {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="none" />
            <TableCell size="small">
              {this.tableSortLabelHtml('id', 'ID')}
            </TableCell>
            <TableCell size="small">
              {this.tableSortLabelHtml('name', 'Name')}
            </TableCell>
            <TableCell size="small">
              {this.tableSortLabelHtml('device_id', 'Device')}
            </TableCell>
            <TableCell size="small">
              {this.tableSortLabelHtml('sensor_id', 'Sensor')}
            </TableCell>
            <TableCell padding="checkbox">Internal</TableCell>
            <TableCell align="right" size="small">Value</TableCell>
            <TableCell size="small">
              {this.tableSortLabelHtml('last_updated', 'Last Updated')}
            </TableCell>
            <TableCell size="small">
              {this.tableSortLabelHtml('description', 'Description')}
            </TableCell>
            <TableCell padding="checkbox">Archived</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {this.sortedTimeseries().map(this.renderTimeseries)}
        </TableBody>
      </Table>
    )
  }
}

export default withStyles(TimeseriesListView.styles)(TimeseriesListView)
